/**
 * Iter 103 — Frontend error capture.
 *
 * Hooks:
 *   • window.onerror                — synchronous JS errors
 *   • window.onunhandledrejection    — async Promise rejections
 *   • axios response interceptor     — HTTP 5xx from any API call
 *   • capture(err, source)           — manual hook for React error boundaries
 *
 * Everything is POST'd to /api/system-logs/frontend-error (unauthenticated
 * is fine — the backend attributes to the current session cookie if any).
 * Client-side throttling limits duplicate storms to 5 events per unique
 * summary per 5-minute window.
 */
import axios from "axios";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

// summary → last-sent timestamps for dedup (max 5 per 5-min window)
const _fingerprints = new Map();
const WINDOW_MS = 5 * 60 * 1000;
const MAX_PER_WINDOW = 5;

function _shouldSend(summary) {
  if (_isNoise(summary)) return false;
  const now = Date.now();
  const stamps = (_fingerprints.get(summary) || []).filter((t) => now - t < WINDOW_MS);
  if (stamps.length >= MAX_PER_WINDOW) return false;
  stamps.push(now);
  _fingerprints.set(summary, stamps);
  return true;
}

function _short(s, max = 480) {
  if (!s) return "";
  const str = String(s).replace(/\s+/g, " ").trim();
  return str.length > max ? str.slice(0, max - 1) + "…" : str;
}

// Iter 275 — browser/library noise that says nothing about our code. The
// "ResizeObserver loop" warning fires whenever an observed element is resized
// inside its own callback (Radix popovers, Recharts) — it is swallowed by the
// browser and harmless, but it used to flood the system log from /customers.
const NOISE = [
  /ResizeObserver loop/i,
  /^Script error\.?$/i,
  /Non-Error promise rejection captured/i,
];

function _isNoise(summary) {
  return NOISE.some((re) => re.test(summary));
}

async function _post(payload) {
  try {
    await axios.post(`${API}/system-logs/frontend-error`, payload, {
      withCredentials: true,
      // Timeout so a hung backend never blocks the user
      timeout: 4000,
      // Never let this call itself trigger another error report
      _skipErrorCapture: true,
    });
  } catch {
    /* silent — never let the capture path throw */
  }
}

/** Manual capture for React error boundaries + ad-hoc call sites. */
export function captureError(err, source = "manual") {
  try {
    const summary = _short(err?.stack || err?.message || String(err));
    if (!summary || !_shouldSend(summary)) return;
    _post({
      level:    "error",
      summary,
      source:   _short(source, 200),
      referer:  typeof window !== "undefined" ? window.location.href : "",
    });
  } catch {
    /* silent */
  }
}

/** Manual capture for warnings (React warnings, deprecations, perf events). */
export function captureWarning(msg, source = "manual") {
  try {
    const summary = _short(msg);
    if (!summary || !_shouldSend(summary)) return;
    _post({
      level:    "warning",
      summary,
      source:   _short(source, 200),
      referer:  typeof window !== "undefined" ? window.location.href : "",
    });
  } catch {
    /* silent */
  }
}

/** Wire the global browser hooks + axios interceptor. Call once at boot. */
export function installFrontendErrorCapture() {
  if (typeof window === "undefined") return;
  if (window.__cnd_error_capture_installed) return;
  window.__cnd_error_capture_installed = true;

  // 1. Synchronous JS errors
  window.addEventListener("error", (event) => {
    // Some events come from resource loads (<img>, <script>) — filter them out
    // unless they have an actual Error object.
    const err = event?.error || event?.message;
    if (!err) return;
    const summary = _short(
      event?.error?.stack ||
      `${event?.message || err} @ ${event?.filename || "?"}:${event?.lineno || 0}:${event?.colno || 0}`
    );
    if (!_shouldSend(summary)) return;
    _post({
      level:    "error",
      summary,
      source:   _short(`window.onerror ${event?.filename || ""}:${event?.lineno || 0}`, 200),
      referer:  window.location.href,
    });
  });

  // 2. Async unhandled rejections
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event?.reason;
    const summary = _short(reason?.stack || reason?.message || String(reason));
    if (!summary || !_shouldSend(summary)) return;
    _post({
      level:    "error",
      summary:  `UnhandledPromiseRejection: ${summary}`,
      source:   _short("window.onunhandledrejection", 200),
      referer:  window.location.href,
    });
  });

  // 3. Axios response interceptor — capture any 5xx from API calls that
  // are NOT the capture endpoint itself. Skips 4xx (expected/handled).
  axios.interceptors.response.use(
    (r) => r,
    (error) => {
      try {
        const cfg = error?.config;
        if (cfg?._skipErrorCapture) return Promise.reject(error);
        const status = error?.response?.status;
        if (status && status >= 500) {
          const url = (cfg?.baseURL || "") + (cfg?.url || "");
          const summary = _short(`HTTP ${status} on ${cfg?.method?.toUpperCase() || "GET"} ${url} — ${error?.response?.data?.detail || error?.message || "server error"}`);
          if (_shouldSend(summary)) {
            _post({
              level:    status >= 500 ? "error" : "warning",
              summary,
              source:   _short("axios 5xx interceptor", 200),
              referer:  window.location.href,
            });
          }
        }
      } catch {
        /* silent */
      }
      return Promise.reject(error);
    }
  );
}
